import { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { 
  Brain, 
  Send, 
  Loader2, 
  BookOpen, 
  Sparkles,
  MessageSquare,
  HelpCircle,
  Trash2
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { createSSELineBuffer, processSSEChunk, flushAndProcessSSE } from '@/lib/sseParser';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

const QUICK_PROMPTS = [
  { label: 'What is QSP?', prompt: 'Explain QuantumSelf Psychology (QSP) and its core principles.' },
  { label: 'Soulprint Sequence', prompt: 'Walk me through the Soulprint Sequence™ framework (Regulate, Recognise, Reflect, Release, Reprogramme, Reconnect).' },
  { label: 'Five Levels', prompt: 'Explain the Five-Level Framework and how to assess clients across all levels.' },
  { label: 'Timeline Work', prompt: 'How do I use timeline therapy with clients? What are the key techniques?' },
  { label: 'Integration', prompt: 'How does QSP integrate evidence-based therapy with holistic approaches?' },
  { label: 'Case Formulation', prompt: 'How do I create a case formulation using the TPP/QSP model?' },
];

export function ClinicianAIAssistant() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const sendMessage = async (messageText: string) => {
    if (!messageText.trim() || isLoading) return;

    const userMessage: Message = {
      role: 'user',
      content: messageText.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error('Please sign in to use the AI assistant');
        return;
      }

      // Build conversation history for context
      const conversationHistory = messages.map(msg => ({
        role: msg.role,
        content: msg.content
      }));

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/clinician-ai-assistant`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            message: messageText.trim(),
            conversationHistory
          }),
        }
      );

      if (response.status === 429) {
        toast.error('Too many requests', {
          description: 'Please wait a moment before trying again.'
        });
        setIsLoading(false);
        return;
      }

      if (response.status === 402) {
        toast.error('Insufficient credits', {
          description: 'You need at least 1 credit to use the AI assistant.'
        });
        setIsLoading(false);
        return;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Request failed: ${response.status}`);
      }

      // Handle streaming response
      const reader = response.body?.getReader();
      if (!reader) throw new Error('No response body');

      const decoder = new TextDecoder();
      let assistantContent = '';
      const sseBuf = createSSELineBuffer();

      // Add placeholder assistant message
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: '',
        timestamp: new Date()
      }]);

      let streamDone = false;

      while (!streamDone) {
        const { done, value } = await reader.read();
        if (done) {
          const final = flushAndProcessSSE(sseBuf);
          assistantContent += final.texts.join('');
          break;
        }

        const { texts, done: isDone } = processSSEChunk(sseBuf, decoder.decode(value, { stream: true }));
        if (texts.length > 0) {
          assistantContent += texts.join('');
          setMessages(prev => {
            const newMessages = [...prev];
            const lastIndex = newMessages.length - 1;
            if (newMessages[lastIndex]?.role === 'assistant') {
              newMessages[lastIndex] = {
                ...newMessages[lastIndex],
                content: assistantContent
              };
            }
            return newMessages;
          });
        }
        if (isDone) { streamDone = true; break; }
      }

      // Remove empty assistant message if no content produced
      if (!assistantContent.trim()) {
        setMessages(prev => prev.filter((_, i) => i !== prev.length - 1 || prev[prev.length - 1].content !== ''));
      }

    } catch (error) {
      console.error('AI Assistant error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to get response');
      // Remove the failed message placeholder
      setMessages(prev => prev.filter((_, i) => i !== prev.length - 1 || prev[prev.length - 1].content !== ''));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendMessage(input);
  };

  const clearConversation = () => {
    setMessages([]);
    toast.success('Conversation cleared');
  };

  return (
    <Card className="h-[700px] flex flex-col">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Brain className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-lg">TPP/QSP Clinical Consultant</CardTitle>
              <p className="text-sm text-muted-foreground">
                Ask about The Psychic Psychologist methodology & QuantumSelf Psychology
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="gap-1">
              <Sparkles className="h-3 w-3" />
              Lovable AI
            </Badge>
            {messages.length > 0 && (
              <Button variant="ghost" size="sm" onClick={clearConversation}>
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="flex-1 flex flex-col overflow-hidden p-4 pt-0">
        {/* Quick prompts */}
        {messages.length === 0 && (
          <div className="mb-4">
            <p className="text-sm text-muted-foreground mb-2 flex items-center gap-1">
              <HelpCircle className="h-4 w-4" />
              Quick questions:
            </p>
            <div className="flex flex-wrap gap-2">
              {QUICK_PROMPTS.map((item, index) => (
                <Button
                  key={index}
                  variant="outline"
                  size="sm"
                  onClick={() => sendMessage(item.prompt)}
                  disabled={isLoading}
                  className="text-xs"
                >
                  {item.label}
                </Button>
              ))}
            </div>
          </div>
        )}

        {/* Messages area */}
        <ScrollArea className="flex-1 pr-4" ref={scrollRef}>
          {messages.length === 0 ? (
            <div className="h-full flex items-center justify-center text-center p-8">
              <div className="space-y-4 max-w-md">
                <BookOpen className="h-12 w-12 mx-auto text-muted-foreground/50" />
                <div>
                  <h3 className="font-medium">TPP/QSP Knowledge Base</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Ask me anything about The Psychic Psychologist methodology, 
                    QuantumSelf Psychology, the Soulprint Sequence™, Five-Level Framework, 
                    or how to apply these in clinical practice.
                  </p>
                </div>
                <div className="text-xs text-muted-foreground bg-muted/50 p-3 rounded-lg">
                  <strong>Note:</strong> This AI uses Lovable AI for clinician support. 
                  The client-facing Therappi AI (therapy sessions) uses Claude separately.
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {messages.map((message, index) => (
                <div
                  key={index}
                  className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] rounded-lg p-3 ${
                      message.role === 'user'
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      {message.role === 'assistant' ? (
                        <Brain className="h-4 w-4" />
                      ) : (
                        <MessageSquare className="h-4 w-4" />
                      )}
                      <span className="text-xs opacity-70">
                        {message.role === 'user' ? 'You' : 'TPP/QSP Consultant'}
                      </span>
                    </div>
                    <div className="text-sm whitespace-pre-wrap">
                      {message.content || (
                        <span className="flex items-center gap-2">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Thinking...
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>

        {/* Input area */}
        <form onSubmit={handleSubmit} className="mt-4 flex gap-2">
          <Textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask about TPP, QSP, Soulprint Sequence™, Five Levels, case formulation..."
            className="min-h-[60px] resize-none"
            disabled={isLoading}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSubmit(e);
              }
            }}
          />
          <Button 
            type="submit" 
            disabled={!input.trim() || isLoading}
            className="h-auto"
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
